import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { serviceRequestsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { FileCheck, Building, Calculator, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { trackServiceSelection } from '@/utils/analytics';

const GSTServicesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedServiceName, setSelectedServiceName] = useState(null);

  const gstServices = [
    {
      name: 'GST Registration - Basic',
      price: 1999,
      icon: Building,
      description: 'Basic GST registration for new businesses',
      features: [
        'GST Registration Filing',
        'Document Verification',
        'Application Submission',
        'ARN Tracking',
        'GSTIN within 7-10 working days'
      ],
      suitableFor: 'New businesses, startups, e-commerce sellers',
      documents: ['PAN Card', 'Aadhaar Card', 'Business Address Proof', 'Bank Account Details', 'Business Registration Certificate']
    },
    {
      name: 'GST Registration - Premium',
      price: 4999,
      icon: Building,
      description: 'Premium GST registration with end-to-end expert assistance',
      features: [
        'End-to-End GST Registration',
        'Dedicated Expert Support',
        'Document Review & Corrections',
        'ARN Tracking & Follow-up',
        'Post-Registration Guidance',
        'Digital signature assistance',
        'State-wise registration support'
      ],
      suitableFor: 'Businesses needing comprehensive support',
      documents: ['PAN Card', 'Aadhaar Card', 'Business Address Proof', 'Bank Account Details', 'Business Registration Certificate'],
      premium: true
    },
    {
      name: 'GST Return Filing',
      price: 999,
      icon: FileCheck,
      description: 'Monthly GST return filing with accurate calculations and timely submission',
      features: [
        'GSTR-1, GSTR-3B filing',
        'Input tax credit optimization',
        'Error-free submissions',
        'Compliance notifications',
        'Annual return support'
      ],
      suitableFor: 'All GST-registered businesses',
      documents: ['Sales Invoices', 'Purchase Invoices', 'Bank Statements', 'Previous Returns'],
      recurring: true
    },
    {
      name: 'GST Consultation',
      price: 1499,
      icon: Calculator,
      description: 'Expert GST advisory for compliance, planning, and optimization',
      features: [
        'GST compliance audit',
        'Input tax credit maximization',
        'GST planning strategies',
        'Notice handling support',
        'Refund assistance'
      ],
      suitableFor: 'Businesses needing GST advisory',
      documents: ['GST Records', 'Financial Statements', 'Previous Returns']
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Timely Filing',
      description: 'Never miss a GST deadline with our automated reminders and quick processing'
    },
    {
      icon: TrendingUp,
      title: 'ITC Optimization',
      description: 'Maximize your input tax credit with expert analysis and proper documentation'
    },
    {
      icon: FileCheck,
      title: 'Error-Free Returns',
      description: 'CA-verified returns to ensure accuracy and avoid penalties or notices'
    },
    {
      icon: Building,
      title: 'Complete Support',
      description: 'From registration to monthly compliance, we handle everything for your business'
    }
  ];

  const gstTypes = [
    { type: 'Regular GST', desc: 'For businesses with turnover > ₹40 lakhs (services) or ₹20 lakhs (goods)' },
    { type: 'Composition Scheme', desc: 'Simplified GST for small businesses with turnover up to ₹1.5 crore' },
    { type: 'E-commerce GST', desc: 'Special provisions for online sellers and marketplace operators' },
    { type: 'Inter-state GST', desc: 'IGST registration for businesses operating across multiple states' }
  ];

  const handleSelectService = async (serviceName) => {
    // If not logged in, redirect to login
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedServiceName(serviceName);

    try {
      // Create a service request with the service name
      const response = await serviceRequestsAPI.create({ 
        service_name: serviceName,
        category: 'gst'
      });
      
      // Find the service price
      const service = gstServices.find(s => s.name === serviceName);
      const servicePrice = service?.price || 0;
      
      // Track service selection conversion
      trackServiceSelection(serviceName, servicePrice);
      
      // Show success and redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      console.error('Error creating service request:', err);
      alert('Failed to select service. Please try again.');
      setLoading(false);
      setSelectedServiceName(null);
    }
  };

  const handleGetStarted = () => {
    navigate('/services');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-20" data-testid="gst-hero">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6" data-testid="gst-title">GST Services</h1>
          <p className="text-xl text-green-100 max-w-3xl mb-8">
            Complete GST registration and filing services for businesses. Stay compliant with expert CA support.
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-50 transition inline-flex items-center gap-2"
            data-testid="start-gst-button"
          >
            Get Started <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our GST Services?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="bg-white p-6 rounded-xl shadow-sm text-center" data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <benefit.icon className="text-green-600" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Our GST Services</h2>
          <p className="text-center text-gray-600 mb-12">Comprehensive GST solutions for your business needs</p>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {gstServices.map((service) => (
              <div key={service.name} className={`rounded-xl p-6 border-2 transition ${service.premium ? 'bg-gradient-to-br from-purple-50 to-green-50 border-purple-300 shadow-xl' : 'bg-gradient-to-br from-green-50 to-white border-green-200 hover:border-green-400'}`} data-testid={`gst-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <service.icon className="text-green-600" size={32} />
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">₹{service.price}</div>
                    {service.recurring && <p className="text-sm text-gray-600">per month</p>}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-2xl font-bold">{service.name}</h3>
                  {service.premium && (
                    <span className="bg-gradient-to-r from-purple-600 to-green-600 text-white text-xs font-bold px-2 py-1 rounded">PREMIUM</span>
                  )}
                </div>
                <p className="text-gray-700 mb-4">{service.description}</p>
                
                <div className="mb-4">
                  <p className="text-sm font-semibold text-green-700 mb-2">Suitable For: {service.suitableFor}</p>
                </div>
                
                <div className="mb-4">
                  <p className="font-semibold mb-2">Key Features:</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mb-4">
                  <p className="font-semibold mb-2 text-sm">Required Documents:</p>
                  <div className="flex flex-wrap gap-2">
                    {service.documents.map((doc) => (
                      <span key={doc} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{doc}</span>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={() => handleSelectService(service.name)}
                  disabled={loading && selectedServiceName === service.name}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    user 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-300 text-gray-600 cursor-pointer hover:bg-gray-400'
                  }`}
                  data-testid={`select-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {loading && selectedServiceName === service.name ? (
                    'Adding...'
                  ) : user ? (
                    'Select This Service'
                  ) : (
                    'Login to Select Service'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GST Types */}
      <section className="py-16 px-4" data-testid="gst-types-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Types of GST Registration</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {gstTypes.map((item) => (
              <div key={item.type} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-green-600" data-testid={`gst-type-${item.type.toLowerCase().replace(/\s+/g, '-')}`}>
                <h3 className="text-xl font-semibold mb-2">{item.type}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GST Compliance */}
      <section className="py-16 px-4 bg-green-50" data-testid="compliance-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">GST Compliance Made Easy</h2>
          <div className="bg-white rounded-xl p-8">
            <h3 className="text-xl font-semibold mb-4">We Handle:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>GSTR-1 (Outward Supplies)</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>GSTR-3B (Monthly Summary)</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>GSTR-9 (Annual Return)</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>Input Tax Credit Reconciliation</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>GST Refund Processing</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span>Notice Response & Appeals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-green-600 text-white" data-testid="final-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get GST Compliant?</h2>
          <p className="text-xl text-green-100 mb-8">Start your GST registration or monthly filing today</p>
          <button
            onClick={handleGetStarted}
            className="bg-white text-green-600 px-12 py-4 rounded-lg text-xl font-semibold hover:bg-green-50 transition"
            data-testid="final-cta-button"
          >
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default GSTServicesPage;